import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Animated, Pressable, View, useWindowDimensions } from 'react-native'
import Svg, { Path, Rect as SvgRect } from 'react-native-svg'
import { Icon } from '@/components/ui/Icon'
import { Text } from '@/components/ui/Text'
import { useThemeColors } from '@/contexts/ThemeContext'

type Rect = {
  x: number
  y: number
  width: number
  height: number
}

type Props = {
  visible: boolean
  testID: string
  title: string
  message: string
  stepIndex: number
  totalSteps: number
  isLastStep: boolean
  isFirstStep: boolean
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  onComplete: () => void
}

const BACKDROP_OPACITY = 0.65
const HIGHLIGHT_PADDING = 10
const TOOLTIP_MAX_WIDTH = 340
const TOOLTIP_MAX_HEIGHT_ESTIMATE = 240
const MOBILE_BREAKPOINT = 640
const MEASURE_POLL_INTERVAL = 50
const MEASURE_POLL_MAX_ATTEMPTS = 40
const FADE_DURATION = 220
const CUTOUT_RADIUS = 20

function findVisibleElement(testID: string): Element | null {
  if (typeof document === 'undefined') return null
  const els = document.querySelectorAll(`[data-testid="${testID}"]`)
  for (const el of Array.from(els)) {
    const rect = el.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return el
  }
  return els.length > 0 ? els[0] : null
}

function findTargetRect(testID: string): Rect | null {
  const el = findVisibleElement(testID)
  if (!el) return null
  const rect = el.getBoundingClientRect()
  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
}

function scrollTargetIntoView(testID: string) {
  const el = findVisibleElement(testID)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
}

type CutoutPathInput = {
  windowWidth: number
  windowHeight: number
  rect: { x: number; y: number; width: number; height: number }
  radius: number
}

function buildCutoutPath({ windowWidth, windowHeight, rect, radius }: CutoutPathInput): string {
  const { x, y, width: w, height: h } = rect
  const r = Math.min(radius, w / 2, h / 2)
  // Outer rect (clockwise) + inner rounded rect (evenodd creates a hole)
  const outer = `M0,0 L${windowWidth},0 L${windowWidth},${windowHeight} L0,${windowHeight}Z`
  const inner = [
    `M${x + r},${y}`,
    `L${x + w - r},${y}`,
    `A${r},${r} 0 0 1 ${x + w},${y + r}`,
    `L${x + w},${y + h - r}`,
    `A${r},${r} 0 0 1 ${x + w - r},${y + h}`,
    `L${x + r},${y + h}`,
    `A${r},${r} 0 0 1 ${x},${y + h - r}`,
    `L${x},${y + r}`,
    `A${r},${r} 0 0 1 ${x + r},${y}`,
    'Z',
  ].join(' ')
  return `${outer} ${inner}`
}

// Render into document.body to escape parent transform contexts (e.g. Expo Router)
// that break position:fixed on mobile browsers
let _createPortal: ((node: React.ReactNode, container: Element) => React.ReactNode) | null = null
function renderInPortal(node: React.ReactNode): React.ReactNode {
  if (typeof document === 'undefined') return node
  if (!_createPortal) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    _createPortal = require('react-dom').createPortal
  }
  return _createPortal ? _createPortal(node, document.body) : node
}

type TooltipPositionInput = {
  target: Rect
  windowWidth: number
  windowHeight: number
  isMobile: boolean
}

function TooltipPosition({ target, windowWidth, windowHeight, isMobile }: TooltipPositionInput) {
  const tooltipWidth = Math.min(TOOLTIP_MAX_WIDTH, windowWidth - 32)
  const padding = isMobile ? 12 : 16

  // On mobile, pin tooltip to the bottom of the viewport to avoid overflow
  if (isMobile) {
    const top = windowHeight - TOOLTIP_MAX_HEIGHT_ESTIMATE - padding
    const left = (windowWidth - tooltipWidth) / 2
    return { top: Math.max(padding, top), left: Math.max(padding, left), below: true }
  }

  const spaceBelow = windowHeight - (target.y + target.height + HIGHLIGHT_PADDING)
  const spaceAbove = target.y - HIGHLIGHT_PADDING

  let top: number
  let below: boolean

  if (spaceBelow >= TOOLTIP_MAX_HEIGHT_ESTIMATE) {
    below = true
    top = target.y + target.height + HIGHLIGHT_PADDING + 8
  } else if (spaceAbove >= TOOLTIP_MAX_HEIGHT_ESTIMATE) {
    below = false
    top = target.y - TOOLTIP_MAX_HEIGHT_ESTIMATE - HIGHLIGHT_PADDING - 8
  } else {
    below = true
    top = Math.max(padding, target.y + target.height + 8)
  }

  // Clamp so tooltip doesn't go off the bottom
  top = Math.min(top, windowHeight - TOOLTIP_MAX_HEIGHT_ESTIMATE - padding)
  top = Math.max(padding, top)

  const maxLeft = windowWidth - tooltipWidth - padding
  let left = target.x + target.width / 2 - tooltipWidth / 2
  left = Math.max(padding, Math.min(left, maxLeft))

  return { top, left, below }
}

export default function CoachMarkOverlay({
  visible,
  testID,
  title,
  message,
  stepIndex,
  totalSteps,
  isLastStep,
  isFirstStep,
  onNext,
  onBack,
  onSkip,
  onComplete,
}: Props) {
  const { t } = useTranslation()
  const colors = useThemeColors()
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const [targetRect, setTargetRect] = useState<Rect | null>(null)
  const [tooltipLayout, setTooltipLayout] = useState({ top: 0, left: 0, below: true })
  const fadeAnim = useRef(new Animated.Value(0)).current
  const slideAnim = useRef(new Animated.Value(12)).current
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Measure the target element
  useEffect(() => {
    if (!visible) {
      setTargetRect(null)
      return
    }

    // Scroll to the target first
    scrollTargetIntoView(testID)

    let attempts = 0
    // Poll until the element is found and measured
    const tryMeasure = () => {
      const rect = findTargetRect(testID)
      if (rect && rect.width > 0 && rect.height > 0) {
        setTargetRect(rect)
        return true
      }
      attempts++
      if (attempts >= MEASURE_POLL_MAX_ATTEMPTS) {
        return true // give up
      }
      return false
    }

    // Wait for scroll animation to settle before measuring
    const initialTimer = setTimeout(() => {
      if (tryMeasure()) return
      pollRef.current = setInterval(() => {
        if (tryMeasure() && pollRef.current) {
          clearInterval(pollRef.current)
        }
      }, MEASURE_POLL_INTERVAL)
    }, 1000)

    return () => {
      clearTimeout(initialTimer)
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [visible, testID, windowWidth, windowHeight])

  // Re-measure after scroll / resize has settled (debounced)
  useEffect(() => {
    if (!visible || typeof window === 'undefined') return

    let scrollTimer: ReturnType<typeof setTimeout> | null = null
    const DEBOUNCE_MS = 120

    const onSettled = () => {
      const rect = findTargetRect(testID)
      if (rect && rect.width > 0 && rect.height > 0) {
        setTargetRect(rect)
      }
    }

    const onScroll = () => {
      if (scrollTimer) clearTimeout(scrollTimer)
      scrollTimer = setTimeout(onSettled, DEBOUNCE_MS)
    }

    const onResize = () => {
      onSettled()
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize, { passive: true })
    return () => {
      if (scrollTimer) clearTimeout(scrollTimer)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [visible, testID])

  // Update tooltip position when target or window changes
  useEffect(() => {
    if (!targetRect) return
    const isMobile = windowWidth < MOBILE_BREAKPOINT
    const pos = TooltipPosition({ target: targetRect, windowWidth, windowHeight, isMobile })
    setTooltipLayout(pos)
  }, [targetRect, windowWidth, windowHeight])

  // Animate in
  useEffect(() => {
    if (visible && targetRect) {
      fadeAnim.setValue(0)
      slideAnim.setValue(12)
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: FADE_DURATION,
          useNativeDriver: false,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: FADE_DURATION,
          useNativeDriver: false,
        }),
      ]).start()
    }
  }, [visible, targetRect, fadeAnim, slideAnim])

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete()
    } else {
      onNext()
    }
  }, [isLastStep, onComplete, onNext])

  if (!visible || typeof document === 'undefined') return null

  const isMobile = windowWidth < MOBILE_BREAKPOINT
  const tooltipWidth = Math.min(TOOLTIP_MAX_WIDTH, windowWidth - 32)

  const highlightRect = targetRect
    ? {
        x: targetRect.x - HIGHLIGHT_PADDING,
        y: targetRect.y - HIGHLIGHT_PADDING,
        width: targetRect.width + HIGHLIGHT_PADDING * 2,
        height: targetRect.height + HIGHLIGHT_PADDING * 2,
      }
    : null

  return renderInPortal(
    <Animated.View
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        opacity: fadeAnim,
      }}
    >
      {/* Semi-transparent backdrop with rounded cutout */}
      {highlightRect ? (
        <>
          {/* Clickable backdrop – clicking outside the tooltip dismisses the tutorial */}
          <Pressable
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            onPress={onSkip}
          />
          <Svg
            width={windowWidth}
            height={windowHeight}
            pointerEvents="none"
            style={{ position: 'absolute', top: 0, left: 0 } as never}
          >
            <Path
              d={buildCutoutPath({
                windowWidth,
                windowHeight,
                rect: highlightRect,
                radius: CUTOUT_RADIUS,
              })}
              fill={`rgba(0,0,0,${BACKDROP_OPACITY})`}
              fillRule="evenodd"
            />
            <SvgRect
              x={highlightRect.x}
              y={highlightRect.y}
              width={highlightRect.width}
              height={highlightRect.height}
              rx={CUTOUT_RADIUS}
              ry={CUTOUT_RADIUS}
              fill="none"
              stroke={colors.primary}
              strokeWidth={2}
            />
          </Svg>
        </>
      ) : (
        /* Full backdrop while waiting for measurement */
        <Pressable
          style={{
            flex: 1,
            backgroundColor: `rgba(0,0,0,${BACKDROP_OPACITY})`,
          }}
          onPress={onSkip}
        />
      )}

      {/* Tooltip card — wrapped in Pressable to prevent backdrop dismiss when clicking tooltip area */}
      <Animated.View
        style={{
          position: 'absolute',
          top: tooltipLayout.top,
          left: tooltipLayout.left,
          width: tooltipWidth,
          maxWidth: windowWidth - 32,
          transform: [{ translateY: slideAnim }],
        }}
      >
        <Pressable onPress={() => {}}>
          <View
            style={{
              backgroundColor: colors.surface,
              borderRadius: 24,
              padding: isMobile ? 16 : 24,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.2,
              shadowRadius: 24,
              elevation: 12,
            }}
          >
            {/* Step indicator */}
            <View className="flex-row items-center justify-between mb-3">
              <View className="flex-row items-center gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <View
                    key={i}
                    style={{
                      width: i === stepIndex ? 20 : 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: i === stepIndex ? colors.primary : colors.border,
                    }}
                  />
                ))}
              </View>
              <Pressable
                onPress={onSkip}
                hitSlop={10}
                className="items-center justify-center rounded-full"
                style={{
                  width: 28,
                  height: 28,
                  backgroundColor: colors.surface,
                  borderWidth: 1.5,
                  borderColor: colors.primary,
                }}
              >
                <Icon name="times" size={14} color={colors.primary} />
              </Pressable>
            </View>

            {/* Title */}
            <Text style={{ color: colors.dark, fontSize: 18, fontWeight: '600', marginBottom: 8 }}>
              {title}
            </Text>

            {/* Message */}
            <Text style={{ color: colors.muted, fontSize: 14, lineHeight: 21, marginBottom: 20 }}>
              {message}
            </Text>

            {/* Navigation buttons */}
            <View className="flex-row items-center justify-between gap-3">
              {!isFirstStep ? (
                <Pressable
                  onPress={onBack}
                  className="flex-row items-center gap-1 rounded-full px-4 py-2.5"
                  style={{ backgroundColor: colors.background }}
                >
                  <Icon name="chevron-left" size={12} color={colors.dark} />
                  <Text
                    style={{ color: colors.dark, fontSize: 13, fontWeight: '600' }}
                    className="uppercase tracking-widest"
                  >
                    {t('tutorial.back')}
                  </Text>
                </Pressable>
              ) : (
                <View />
              )}

              <Pressable
                onPress={handleNext}
                className="flex-row items-center gap-1.5 rounded-full px-5 py-2.5"
                style={{ backgroundColor: colors.primary }}
              >
                <Text
                  style={{ color: colors.white, fontSize: 13, fontWeight: '600' }}
                  className="uppercase tracking-widest"
                >
                  {isLastStep ? t('tutorial.done') : t('tutorial.next')}
                </Text>
                {!isLastStep && <Icon name="chevron-right" size={12} color={colors.white} />}
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Animated.View>
    </Animated.View>
  )
}
